import type { State } from "../State";
import { readPdfLicensePolicy } from "../pdf/PdfLicensePolicy";
import { showErrToast, showToast } from "../components/Toasts";
import { pdfChapterTitle, selectPdfChapter, availablePdfConfig, excelChapter, isExcelChapter, type PdfExportConfig, type PdfApplicationMode, type PdfChapterId } from "../pdf/PdfExportConfig";
import { savePdfExportConfig } from "../pdf/PdfExportSettings";
import { resolvePdfDocumentBrand } from "../pdf/PdfDocumentHeader";
import { collectExcelReport } from "./excelReports";
import { createDocumentWorkbook, type WorkbookDocument } from "./documentWorkbook";
import { createExcelLicensePreview } from "./excelLicensePreview";
import { loadExcelDocumentLogo } from "./loadExcelDocumentLogo";
let exporting = false;

export async function exportProjectExcel(app: State, input: PdfExportConfig, mode: PdfApplicationMode, singleChapterId?: PdfChapterId): Promise<void> {
  if (exporting) return;
  exporting = true;
  try {
    const { preview } = readPdfLicensePolicy(app.projectLicense);
    const selected = availablePdfConfig(selectPdfChapter(input, singleChapterId), app);
    const chapters = selected.chapters.filter(chapter => chapter.enabled && isExcelChapter(chapter)).map(excelChapter);
    if (!chapters.length) throw new Error("Bitte mindestens ein Listendokument auswählen.");
    // Share label preferences while preserving the complete PDF selection and chapter representations.
    const { readPdfExportConfig } = await import("../pdf/PdfExportSettings");
    const saved = readPdfExportConfig(app, mode);
    savePdfExportConfig(app, mode, { ...input, chapters: saved.chapters });
    for (const device of app.allDevices()) await device.template?.knxProductData.createRefMap(device);
    const documents: WorkbookDocument[] = [];
    for (const chapter of chapters) {
      const report = await collectExcelReport(app, chapter);
      documents.push({ title: pdfChapterTitle(chapter), report: preview ? createExcelLicensePreview(report) : report });
    }
    const brand = resolvePdfDocumentBrand(app);
    const logo = await loadExcelDocumentLogo(brand.logoSource);
    const bytes = await createDocumentWorkbook(app.projectMetadata.name, brand.name, documents, new Date(), logo,
      { productName: mode === "main" ? "Projekt-Dokumentation" : "Concept One", author: app.settings?.userName });
    const blob = new Blob([Uint8Array.from(bytes)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement("a"); link.href = url;
      link.download = `${[app.projectMetadata.name, singleChapterId ? documents[0].title : "Dokumentation"].join("_").replace(/[<>:"/\\|?*\x00-\x1f]/g,"_")}.xlsx`;
      link.click();
    } finally { window.setTimeout(() => URL.revokeObjectURL(url), 1000); }
    await showToast({ title: "Excel-Export", text: preview ? "Die Excel-Dokumentation wurde als Lizenzvorschau erstellt." : "Die Excel-Dokumentation wurde erstellt." });
  } catch (error) { showErrToast(error instanceof Error ? error.message : String(error), "Excel-Export"); }
  finally { exporting = false; }
}
